import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl font-bold text-[#012d5c] mb-4 pb-2 border-b border-gray-100">
      {title}
    </h2>
    <div className="space-y-4 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "Privacy Policy | Integro Clinics";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Read the Integro Clinics Privacy Policy. Understand how we collect, use and protect your personal data in compliance with UK GDPR.";
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <div
        className="bg-[#012d5c] py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(1,45,92,0.88),rgba(1,45,92,0.88)),url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <span className="inline-block bg-[#4caf50] text-white text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-5">
            Legal
          </span>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Your privacy is our priority. This policy explains how Integro Clinics collects, uses and protects your personal data.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">

            <Section title="Introduction">
              <p>
                Integro Clinics respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, store and protect information about you when you use our website or services.
              </p>
              <p>
                We may update this policy from time to time. Any significant changes will be communicated to you via email or through our website. Please read this policy carefully to understand how your personal data is handled.
              </p>
            </Section>

            <Section title="What We Do">
              <p>
                Integro Clinics is a UK-based specialist medical cannabis clinic. We provide specialist clinical assessments and, where clinically appropriate following MDT review, access to medical cannabis treatment. We bring together patients and specialist clinicians to provide safe, regulated healthcare services.
              </p>
            </Section>

            <Section title="Our Role as Data Controller">
              <p>
                For the purposes of the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018, Integro Clinics acts as the data controller in respect of personal data collected through our website and clinical services. This means we are responsible for deciding how and why your personal data is processed.
              </p>
              <p>
                We are registered with the Information Commissioner's Office (ICO) and ensure that our data handling practices are compliant with applicable UK data protection law.
              </p>
            </Section>

            <Section title="Information We Collect">
              <p>We may collect the following categories of personal data:</p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Identity information: your name, date of birth, gender and other demographic details.",
                  "Contact information: your email address, telephone number and postal address.",
                  "Medical information: details of your health conditions, medical history, current and previous medications, and other health-related information required to provide clinical services.",
                  "Usage data: information about how you interact with our website, including device information, browser type and IP address.",
                  "Identity verification: a copy of a valid form of identification where required for registration.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="How We Collect Your Data">
              <p>We collect your personal data in the following ways:</p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Direct interactions: when you register on our patient portal, complete forms, submit an eligibility check, or communicate with us directly.",
                  "Automated technologies: through cookies and similar tracking technologies when you use our website.",
                  "Healthcare integrations: where you provide consent, we may access relevant health information from third-party healthcare systems or your existing medical records.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="How We Use Your Data">
              <p>We process your personal data for the following purposes:</p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Providing clinical services: to deliver the assessments and services you have requested.",
                  "Patient safety and care: to enable our clinical team to make informed prescribing decisions in your best interests.",
                  "Improving services: to analyse and improve our services, including patient care pathways and our technology platform.",
                  "Regulatory compliance: to meet our legal and regulatory obligations as a CQC-registered healthcare provider.",
                  "Communications: to contact you about appointments, outcomes and service updates relevant to your care.",
                  "Marketing: we will only send you information relevant to your treatment or service. We will not use your data for general marketing purposes without your explicit consent.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="Legal Grounds for Processing">
              <p>We process your personal data on the following legal grounds under UK GDPR:</p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Contractual necessity: to provide the clinical and administrative services you have requested.",
                  "Legitimate interests: to operate and improve our services, where your interests and rights do not override those interests.",
                  "Consent: where you have provided explicit consent, particularly for the processing of special category data such as health information.",
                  "Legal obligation: to comply with applicable healthcare regulations, including our obligations as a CQC-registered provider.",
                  "Vital interests or substantial public interest: in limited circumstances where processing is necessary to protect health.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="Information Sharing">
              <p>We may share your personal data with:</p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Clinical staff: specialist clinicians, MDT members and other healthcare professionals involved in your care.",
                  "Regulated pharmacies: for the purpose of dispensing any approved prescriptions.",
                  "Regulatory authorities: including the CQC, MHRA and other bodies where we have a legal obligation to report.",
                  "Third-party service providers: for operational purposes such as secure data processing, technology infrastructure and service delivery, always under appropriate data processing agreements.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 font-medium text-[#012d5c]">
                We will never sell or rent your personal information to third parties.
              </p>
            </Section>

            <Section title="Data Security">
              <p>
                We take the security of your personal data seriously. We use industry-standard technical and organisational measures to protect your data from unauthorised access, loss, misuse or alteration. Access to patient data is restricted to authorised clinical and administrative personnel involved in your care.
              </p>
              <p>
                While we implement robust security measures, no data transmission over the internet can be guaranteed to be entirely secure. If you have concerns about the security of your data, please contact us using the details below.
              </p>
            </Section>

            <Section title="Data Retention">
              <p>
                We retain your personal data for as long as necessary to provide our services and to comply with our legal and regulatory obligations. Clinical records are retained in accordance with NHS and healthcare regulatory guidance. Where data is no longer required, it is securely deleted or anonymised.
              </p>
            </Section>

            <Section title="Your Rights Under UK GDPR">
              <p>You have the following rights in relation to your personal data:</p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Right of access: you may request a copy of the personal data we hold about you.",
                  "Right to rectification: you may ask us to correct inaccurate or incomplete data.",
                  "Right to erasure: you may request deletion of your data in certain circumstances.",
                  "Right to restriction: you may ask us to restrict processing of your data in certain situations.",
                  "Right to object: you may object to certain types of processing, including direct marketing.",
                  "Right to data portability: you may request that we transfer your data to another service provider in a structured, machine-readable format.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at{" "}
                <a href="mailto:office@integroclinics.com" className="text-[#005eb8] hover:underline">
                  office@integroclinics.com
                </a>
                . You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe your data has been processed unlawfully.
              </p>
            </Section>

            <Section title="Cookies">
              <p>
                Our website uses cookies to improve your experience and to understand how our website is used. Cookies are small text files placed on your device by your browser. You can control cookie settings through your browser preferences. Disabling cookies may affect the functionality of certain parts of our website.
              </p>
            </Section>

            <Section title="Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our services or applicable law. Where changes are significant, we will notify you by email or through a prominent notice on our website. We encourage you to review this policy periodically.
              </p>
            </Section>

            <Section title="Contact Us">
              <p>
                If you have any questions or concerns about this Privacy Policy or how we handle your personal data, please contact us:
              </p>
              <div className="mt-4 bg-[#f8fafc] border border-gray-100 rounded-xl p-6 space-y-2">
                <p><strong>Email:</strong>{" "}
                  <a href="mailto:office@integroclinics.com" className="text-[#005eb8] hover:underline">
                    office@integroclinics.com
                  </a>
                </p>
              </div>
            </Section>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
